/**
 * Single source of truth for the cron-job schedules wired up in
 * workers/cron-jobs/wrangler.toml.
 *
 * Every admin page that displays "next run" countdowns reads from here so
 * they stay in lock-step with the actual deployed cron triggers. If you ever
 * change a schedule, update the cron expression below AND redeploy
 * `workers/cron-jobs` — the UI countdown derives from the expression here so
 * it follows the change automatically.
 */

export interface CronJob {
  /** Stable id, useful as React key. */
  id: "process-automations" | "send-scheduled-emails" | "scrape-rba-rates";
  /** Short human label shown in the UI. */
  label: string;
  /** Sub-label / what the job does. */
  description: string;
  /** Cadence expression — purely descriptive, not parsed. */
  cadence: CronCadence;
  /** Friendly text shown under the countdown. */
  cadenceLabel: string;
}

type CronCadence =
  | { kind: "daily-utc-hour"; hour: number }
  | { kind: "every-n-minutes"; minutes: number };

/**
 * Mirrors workers/cron-jobs/wrangler.toml triggers.
 */
export const CRON_JOBS: readonly CronJob[] = [
  {
    id: "process-automations",
    label: "Process Automations",
    description: "Scan birthdays, anniversaries",
    cadence: { kind: "daily-utc-hour", hour: 14 },
    cadenceLabel: "Daily at 12:00 AM AEST",
  },
  {
    id: "send-scheduled-emails",
    label: "Send Emails + Campaigns",
    description: "Deliver queued emails",
    cadence: { kind: "every-n-minutes", minutes: 5 },
    cadenceLabel: "Every 5 minutes",
  },
  {
    id: "scrape-rba-rates",
    label: "Scrape RBA Rates",
    description: "Pull latest cash-rate decisions",
    cadence: { kind: "daily-utc-hour", hour: 0 },
    cadenceLabel: "Daily at 10:00 AM AEST",
  },
];

export function getCronJob(id: CronJob["id"]): CronJob {
  const job = CRON_JOBS.find(j => j.id === id);
  if (!job) throw new Error(`Unknown cron job id: ${id}`);
  return job;
}

/**
 * Returns the absolute timestamp (ms epoch) of the next scheduled run.
 *
 * For "every-n-minutes" we round UP to the next slot. For "daily-utc-hour"
 * we pick today's UTC slot, rolling to tomorrow if it's already past.
 */
export function nextRunMs(cadence: CronCadence, fromMs: number): number {
  const now = new Date(fromMs);
  if (cadence.kind === "daily-utc-hour") {
    const d = new Date(now);
    d.setUTCHours(cadence.hour, 0, 0, 0);
    if (d.getTime() <= fromMs) d.setUTCDate(d.getUTCDate() + 1);
    return d.getTime();
  }
  // every-n-minutes
  const d = new Date(now);
  d.setUTCSeconds(0, 0);
  const slot = Math.floor(d.getUTCMinutes() / cadence.minutes) * cadence.minutes + cadence.minutes;
  d.setUTCMinutes(slot);
  if (d.getTime() <= fromMs) {
    d.setUTCMinutes(d.getUTCMinutes() + cadence.minutes);
  }
  return d.getTime();
}

/**
 * Format ms-difference as "Hh MMm SSs" / "MMm SSs" / "SSs".
 *
 * Used by per-job countdowns. Pads minutes/seconds to two digits so the
 * countdown doesn't visually jitter as it ticks.
 */
export function formatCountdown(targetMs: number, nowMs: number): string {
  const diff = Math.max(0, Math.floor((targetMs - nowMs) / 1000));
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}
